import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { EmbeddedJourneyPrefill, WayflyerUiSdk } from '@wf-financing/ui-sdk';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import { GET_WAYFLYER_COMPANY_SESSION } from 'gql/queries';
import { userRoles } from 'types/user';

const WAYFLYER_CTA_TARGET_ID = 'wayflyer-capital-cta';
const WAYFLYER_LANGUAGES = ['en', 'de', 'fr', 'nl', 'sv', 'es'];
let wayflyerTargetSequence = 0;

const createWayflyerTargetId = (): string =>
  `${WAYFLYER_CTA_TARGET_ID}-${++wayflyerTargetSequence}`;

type WayflyerCompanySessionData = {
  wayflyerCompanySession?: {
    companyToken: string;
    isSandbox: boolean;
  } | null;
};

type WayflyerCompanySessionVariables = {
  storeId: string;
};

type WayflyerCtaHostProps = {
  companyToken: string;
  isSandbox: boolean;
  language: string;
  prefill: EmbeddedJourneyPrefill;
  storeId: string;
};

const getWayflyerLanguage = (locale?: string): string => {
  const language = locale?.split(/[-_]/)[0]?.toLowerCase();
  return language && WAYFLYER_LANGUAGES.includes(language) ? language : 'en';
};

const WayflyerCtaHost: React.FC<WayflyerCtaHostProps> = ({
  companyToken,
  isSandbox,
  language,
  prefill,
  storeId,
}) => {
  const prefillRef = useRef(prefill);
  prefillRef.current = prefill;
  // The SDK caches its React root by target ID. Keeping the ID inside this
  // host makes ordinary rerenders stable while a removed host gets a fresh ID.
  const [targetId] = useState(createWayflyerTargetId);
  const [hasSdkError, setHasSdkError] = useState(false);

  useEffect(() => {
    const target = document.getElementById(targetId);
    if (!target) return;

    let cancelled = false;
    target.innerHTML = '';
    setHasSdkError(false);

    WayflyerUiSdk.loadSdk(companyToken, {
      isSandbox,
      language,
      skipAnimations:
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ??
        false,
    })
      .then((sdk) => {
        if (!cancelled) {
          sdk.mountCta(targetId, async () => prefillRef.current);
        }
      })
      .catch(() => {
        if (!cancelled) {
          console.warn('Wayflyer financing is currently unavailable.');
          setHasSdkError(true);
        }
      });

    return () => {
      cancelled = true;
      target.innerHTML = '';
    };
  }, [companyToken, isSandbox, language, storeId, targetId]);

  return (
    <div
      id={targetId}
      className="mt-6 w-full"
      style={{ minHeight: 1 }}
      aria-label="Business financing from Wayflyer"
      hidden={hasSdkError}
    />
  );
};

export const WayflyerCapitalBanner: React.FC = () => {
  const { storeId, storeProperties } = useStore();
  const { role, user } = useUser();
  const adminStorageKey =
    process.env.REACT_APP_ADMIN_STORAGE_KEY || 'admin-context';
  const isSupportSession = useMemo(() => {
    try {
      return Boolean(localStorage.getItem(adminStorageKey));
    } catch {
      return true;
    }
  }, [adminStorageKey]);
  const isEligible = Boolean(
    storeId &&
      storeProperties?.id === storeId &&
      storeProperties?.features?.WAYFLYER_EMBEDDED_FINANCE === true &&
      role === userRoles.ADMIN &&
      user?.defaultRole !== userRoles.SHIPAID_ADMIN &&
      !isSupportSession,
  );

  const { data, error, loading } = useQuery<
    WayflyerCompanySessionData,
    WayflyerCompanySessionVariables
  >(GET_WAYFLYER_COMPANY_SESSION, {
    variables: { storeId: storeId || '' },
    skip: !isEligible,
    fetchPolicy: 'network-only',
  });

  const prefill = useMemo<EmbeddedJourneyPrefill>(() => {
    const primaryStoreUrl =
      storeProperties?.url ||
      (storeProperties?.store ? `https://${storeProperties.store}` : undefined);

    return {
      ...(user?.email ? { email: user.email } : {}),
      ...(storeProperties?.name ? { companyName: storeProperties.name } : {}),
      ...(primaryStoreUrl ? { primaryStoreUrl } : {}),
    };
  }, [
    storeProperties?.name,
    storeProperties?.store,
    storeProperties?.url,
    user?.email,
  ]);
  const session = data?.wayflyerCompanySession;
  const language = getWayflyerLanguage(user?.locale);
  if (!isEligible || loading || error || !session?.companyToken) return null;

  return (
    <WayflyerCtaHost
      companyToken={session.companyToken}
      isSandbox={session.isSandbox}
      language={language}
      prefill={prefill}
      storeId={storeId || ''}
    />
  );
};
